<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NooviConnectConnect from 'dashboard/components-next/noovi-connect/NooviConnectConnect.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const inboxName = ref('');
const createdInboxId = ref(null);

const uiFlags = useMapGetter('inboxes/getUIFlags');
const accountId = computed(() => route.params.accountId);

const v$ = useVuelidate({ inboxName: { required } }, { inboxName });

const goToAgents = () => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: createdInboxId.value },
  });
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  try {
    const channel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value?.trim(),
      channel: { type: 'noovi_connect_whatsapp' },
    });
    createdInboxId.value = channel.id;
  } catch (error) {
    useAlert(
      error.message ||
        t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.API.ERROR_MESSAGE')
    );
  }
};

const onConnected = () => {
  useAlert(t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CONNECTED'));
  goToAgents();
};
</script>

<template>
  <!-- Step 1: create inbox -->
  <form
    v-if="!createdInboxId"
    class="flex flex-col flex-wrap mx-0"
    @submit.prevent="createChannel()"
  >
    <div class="mb-6">
      <h2 class="mb-2 text-lg font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex-grow-0 flex-shrink-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="w-full mt-6">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.SUBMIT_BUTTON')"
      />
    </div>
  </form>

  <!-- Step 2: connect (QR / code) -->
  <NooviConnectConnect
    v-else
    :account-id="accountId"
    :inbox-id="createdInboxId"
    show-skip-action
    @connected="onConnected"
    @skip="goToAgents"
  />
</template>
